if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.infoColumn = document
          .getElementById(`ProductInfo-${this.sectionId}`)
          ?.closest('.product__info-wrapper');
        this.mediaColumn = this.infoColumn?.closest('.product')?.querySelector('.product__media-wrapper');
        this.stickyButton = this.querySelector('.sticky-atc__button');
        this.stickyPrice = this.querySelector('.sticky-atc__price');
        this.desktopQuery = window.matchMedia('(min-width: 990px)');
        this.onIntersect = this.onIntersect.bind(this);
        this.onResize = this.onResize.bind(this);
        this.positionToInfoColumn = this.positionToInfoColumn.bind(this);
      }

      connectedCallback() {
        if (!this.mainButton) return;

        // Reveal the bar only once the main buy button has scrolled ABOVE the
        // viewport (i.e. we have already seen it), and hide it again when the
        // button is visible or still below the fold.
        this.observer = new IntersectionObserver(this.onIntersect, { threshold: 0 });
        this.observer.observe(this.mainButton);

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!event.data || event.data.sectionId !== this.sectionId) return;
          this.updateFromHtml(event.data.html);
        });

        // Align the bar to the real info column so it matches any media size.
        this.positionToInfoColumn();
        window.addEventListener('resize', this.onResize);
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.positionToInfoColumn);
          if (this.infoColumn) this.resizeObserver.observe(this.infoColumn);
          if (this.mediaColumn) this.resizeObserver.observe(this.mediaColumn);
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        window.removeEventListener('resize', this.onResize);
        if (this.rafId) cancelAnimationFrame(this.rafId);
      }

      onResize() {
        if (this.rafId) cancelAnimationFrame(this.rafId);
        this.rafId = requestAnimationFrame(this.positionToInfoColumn);
      }

      // Position the bar via CSS variables: its LEFT edge lines up with the
      // right edge of the visible product media (the image), its RIGHT edge
      // with the info column's right edge, and its content indent keeps the
      // title aligned under the product description. Below the desktop
      // breakpoint the vars are cleared so the bar falls back to full width.
      positionToInfoColumn() {
        if (this.desktopQuery.matches && this.infoColumn) {
          const info = this.infoColumn.getBoundingClientRect();

          // Right edge of the visible media (the fixed-crop .thumbnail image);
          // fall back to the media column box, then to the info column left.
          const mediaEl = this.mediaColumn
            ? this.mediaColumn.querySelector('.thumbnail') || this.mediaColumn
            : null;
          const left = mediaEl ? mediaEl.getBoundingClientRect().right : info.left;

          this.style.setProperty('--sticky-atc-left', `${left}px`);
          this.style.setProperty('--sticky-atc-width', `${info.right - left}px`);
          // Fixed content indent for the bar (title starts 140px in).
          this.style.setProperty('--sticky-atc-pad-left', '140px');
          // Background bleeds to the viewport edge, but keep the button aligned
          // with the info column's right edge instead of glued to the screen.
          this.style.setProperty('--sticky-atc-pad-right', `${Math.max(window.innerWidth - info.right, 0)}px`);
        } else {
          this.style.removeProperty('--sticky-atc-left');
          this.style.removeProperty('--sticky-atc-width');
          this.style.removeProperty('--sticky-atc-pad-left');
          this.style.removeProperty('--sticky-atc-pad-right');
        }
      }

      onIntersect(entries) {
        const entry = entries[0];
        if (!entry) return;
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', scrolledPast);
        this.setAttribute('aria-hidden', scrolledPast ? 'false' : 'true');
      }

      updateFromHtml(html) {
        if (!html) return;
        // `variantChange` publishes an already-parsed Document (see global.js
        // renderProductInfo). Support a raw HTML string too, just in case.
        const doc = typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : html;

        // Sync price
        const newPrice = doc.getElementById(`price-${this.sectionId}`);
        if (newPrice && this.stickyPrice) {
          this.stickyPrice.innerHTML = newPrice.innerHTML;
        }

        // Sync button availability + label from the freshly rendered main button
        const newMainButton = doc.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (newMainButton && this.stickyButton) {
          const disabled = newMainButton.hasAttribute('disabled');
          this.stickyButton.toggleAttribute('disabled', disabled);
          const newLabel = newMainButton.querySelector('span');
          const stickyLabel = this.stickyButton.querySelector('span');
          if (newLabel && stickyLabel) {
            stickyLabel.textContent = newLabel.textContent.trim();
          }
        }

        // A variant change can reflow the info column height/width.
        this.positionToInfoColumn();
      }
    }
  );
}
